import React, { useEffect, useRef, useState } from "react";
import { __ } from "services/Translator";
import { URGENT_THRESHOLD_MS } from "./weeklyRaceUtils";

const DIGITS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

// Visually-hidden but announced by screen readers. Inlined so the live-region
// works regardless of whether a scoped .sr-only utility is in the ancestor tree.
const SR_ONLY = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
};

function DigitReel({ value }) {
  const reelRef = useRef(null);
  const prevRef = useRef(value);

  useEffect(() => {
    const el = reelRef.current;
    if (!el || prevRef.current === value) return;
    const cur = prevRef.current;
    if (value < cur) {
      el.style.transition = "none";
      el.style.transform = "translateY(calc(-10em))";
      void el.offsetHeight;
      el.style.transition = "";
      requestAnimationFrame(() => {
        el.style.transform = `translateY(calc(${-value}em))`;
      });
    } else {
      el.style.transform = `translateY(calc(${-value}em))`;
    }
    prevRef.current = value;
  }, [value]);

  return (
    <div className="cd-digit">
      <div className="cd-reel" ref={reelRef} data-d={value}>
        {DIGITS.map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>
    </div>
  );
}

function TimeUnit({ tens, ones, label }) {
  return (
    <div className="cd-unit">
      <div className="cd-box">
        <DigitReel value={tens} />
        <DigitReel value={ones} />
      </div>
      <div className="ulbl">{label}</div>
    </div>
  );
}

function Separator() {
  return (
    <div className="cd-sep" aria-hidden="true">
      <b />
      <b />
    </div>
  );
}

export default function RaceCountdown({ endAt }) {
  const [parts, setParts] = useState({ days: 0, hrs: 0, min: 0, sec: 0 });
  const [urgent, setUrgent] = useState(false);
  const [announce, setAnnounce] = useState("");
  const lastMinuteRef = useRef(null);
  const lastUrgentRef = useRef(false);

  useEffect(() => {
    if (!endAt) return undefined;
    const target = endAt instanceof Date ? endAt : new Date(endAt);

    const tick = () => {
      const d = Math.max(0, target.getTime() - Date.now());
      const days = Math.floor(d / 86400000);
      const hrs = Math.floor((d % 86400000) / 3600000);
      const min = Math.floor((d % 3600000) / 60000);
      const sec = Math.floor((d % 60000) / 1000);
      setParts({ days, hrs, min, sec });

      const isUrgent = d > 0 && d <= URGENT_THRESHOLD_MS;
      setUrgent(isUrgent);

      // Announce a human sentence at most once per minute (or on urgent onset)
      // so screen readers are not flooded by the per-second visual updates.
      const minuteKey = Math.floor(d / 60000);
      const urgentOnset = isUrgent && !lastUrgentRef.current;
      if (minuteKey !== lastMinuteRef.current || urgentOnset) {
        lastMinuteRef.current = minuteKey;
        const sentence = `${days} ${__("Days")}, ${hrs} ${__("Hours")}, ${min} ${__(
          "Minutes"
        )} ${__("remaining in the race")}`;
        setAnnounce(
          urgentOnset ? `${__("Final stretch!")} ${sentence}` : sentence
        );
      }
      lastUrgentRef.current = isUrgent;
    };

    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [endAt]);

  if (!endAt) return null;

  return (
    <section className={`panel bg-clock cd-card${urgent ? " urgent" : ""}`}>
      <div className="sr-only" style={SR_ONLY} role="timer" aria-live="polite">
        {announce}
      </div>
      <div className="cd-conic" aria-hidden="true" />
      <div className="cd-lbl">
        <span className="live" aria-hidden="true" />
        {__("Time left on race")}
      </div>
      <div className="cd-clock-row" aria-hidden="true">
        <TimeUnit
          tens={Math.floor(parts.days / 10) % 10}
          ones={parts.days % 10}
          label={__("Days")}
        />
        <Separator />
        <TimeUnit
          tens={Math.floor(parts.hrs / 10) % 10}
          ones={parts.hrs % 10}
          label={__("Hours")}
        />
        <Separator />
        <TimeUnit
          tens={Math.floor(parts.min / 10) % 10}
          ones={parts.min % 10}
          label={__("Minutes")}
        />
        <Separator />
        <TimeUnit
          tens={Math.floor(parts.sec / 10) % 10}
          ones={parts.sec % 10}
          label={__("Seconds")}
        />
      </div>
    </section>
  );
}
